/**
 * Benchmark System Types
 *
 * Shared TypeScript types for the benchmark system. Used by:
 * - `@chatsdet/benchmark` package (expectations)
 * - `@chatsdet/db` (repository types)
 * - `apps/api/src/benchmark` (service, routes, scoring)
 * - `apps/web/src/ui/pages/superadmin` (frontend UI)
 *
 * @module @chatsdet/types/benchmark
 * @see docs/api/services/benchmark.md
 */

// Job types that can be benchmarked
export type BenchmarkJobType = 'workflow_suite';

// Run status
export type BenchmarkRunStatus = 'pending' | 'running' | 'completed' | 'failed' | 'timeout';

// Trigger type
export type BenchmarkTriggerType = 'scheduled' | 'manual';

// ---------------------------------------------------------------------------
// Workflow Suite Benchmark Types
// ---------------------------------------------------------------------------

export type BenchmarkWorkflowRunStatus = 'pending' | 'running' | 'completed' | 'failed' | 'skipped';

/**
 * Configuration for workflow suite benchmarks (stored in jobConfig jsonb)
 */
export interface BenchmarkWorkflowSuiteConfig {
  runsPerWorkflow?: number;
  runTimeoutMinutes?: number;
  suiteTimeoutHours?: number;
  sendSlackSummary?: boolean;
}

import { z } from 'zod';

export const benchmarkWorkflowSuiteConfigSchema = z.object({
  runsPerWorkflow: z.number().int().min(1).max(20).optional(),
  runTimeoutMinutes: z.number().int().min(1).max(720).optional(),
  suiteTimeoutHours: z.number().int().min(1).max(24).optional(),
  sendSlackSummary: z.boolean().optional(),
});

/**
 * Per-node metrics captured during a benchmark workflow run
 */
export interface BenchmarkNodeMetrics {
  nodeId: string;
  nodeName: string;
  nodeType: string;
  durationMs: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  status: string;
  cacheOutcome: 'hit' | 'miss' | 'fallback' | 'skipped' | null;
  cacheHitSource: 'replay' | 'recovery' | 'l2_gap' | 'wait_shortcut' | null;
  cacheOutcomeReason: string | null;
  compilationScore?: number | null;
  gapCount?: number | null;
  repairApplied?: boolean | null;
  maxInputTokens: number | null;
  contextWindowTokens: number | null;
}

/**
 * Aggregated metrics for a single benchmark workflow run
 */
export interface BenchmarkWorkflowRunMetrics {
  id: string;
  benchmarkRunId: string;
  sourceWorkflowId: string;
  copyWorkflowId: string;
  workflowName: string;
  runNumber: number;
  workflowRunId: string | null;
  status: BenchmarkWorkflowRunStatus;
  isCached: boolean;
  durationMs: number | null;
  totalTokens: number | null;
  inputTokens: number | null;
  outputTokens: number | null;
  totalNodes: number | null;
  successfulNodes: number | null;
  failedNodes: number | null;
  nodeMetrics: BenchmarkNodeMetrics[] | null;
  errorMessage: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

/**
 * Result of importing a workflow into the benchmark org
 */
export interface BenchmarkImportResult {
  workflowId: string;
  workflowName: string;
  propertyId: string;
  propertyName: string;
  credentialMappings: Array<{
    sourceCredentialId: string;
    newCredentialId: string;
    credentialName: string;
  }>;
}

// ---------------------------------------------------------------------------
// Environment Snapshot (captured at suite run time for comparison)
// ---------------------------------------------------------------------------

/**
 * Structured snapshot of the full execution environment captured at benchmark run time.
 * Enables multi-dimensional comparison between runs to attribute result changes.
 */
export interface EnvironmentSnapshot {
  /** Workflow-relevant prompt versions and content hashes */
  prompts: Record<string, { version: number; contentHash: string }>;
  /** Resolved AI provider/model configuration */
  modelConfig: {
    baseProvider: string;
    defaultModel: string;
    contextWindowTokens: number;
    providerOverride: string | null;
    modelOverride: string | null;
  };
  /** Per-workflow flow graph metadata (keyed by source workflow ID) */
  workflows: Record<
    string,
    {
      name: string;
      snapshotVersion: number;
      snapshotHash: string;
      nodeCount: number;
      edgeCount: number;
    }
  >;
  /** Benchmark config metadata at run time */
  benchmarkConfig: {
    version: number;
    provider: string | null;
    model: string | null;
    promptVersion: string | null;
    jobConfig: Record<string, unknown>;
  };
}

export interface BenchmarkWorkflowTrendPoint {
  date: string;
  benchmarkRunId: string;
  /** Null for ad-hoc A/B runs (no saved config). */
  configId: string | null;
  workflowName: string;
  runNumber: number;
  isCached: boolean;
  status: BenchmarkWorkflowRunStatus;
  triggerType: BenchmarkTriggerType;
  durationMs: number | null;
  totalTokens: number | null;
  successfulNodes: number | null;
  failedNodes: number | null;
  totalNodes: number | null;
  cachedSteps: number | null;
  l2RecoverySteps: number | null;
  ejectedSteps: number | null;
  agentSteps: number | null;
  skippedSteps: number | null;
}
